/**
 * Zoom join-URL utilities.
 *
 * The primary mobile join strategy uses the normal Zoom HTTPS join URL
 * (zoom.us/j/...). On iOS and Android, Zoom has registered these URLs as
 * universal/app links: when the Zoom app is installed the OS opens it
 * directly; when it is not, the browser falls back to the Zoom web join page.
 *
 * We deliberately do NOT use the `zoommtg://` or `zoomus://` custom URL
 * schemes as the primary approach because:
 *   - They require explicit URL-scheme handling in the WebView
 *   - They are undocumented / subject to change
 *   - They do not gracefully fall back when the app is absent
 *
 * Security notes:
 *   - Only `https:` URLs on `zoom.us` (or configured custom domains) are accepted
 *   - `javascript:`, `data:`, `file:` and other non-HTTP schemes are rejected
 *   - `start_url` (host tokens) must never be passed to this module — they
 *     are for provider-side use only and should not reach client-facing UI
 *   - Query params (pwd, tk, zak) are preserved verbatim to avoid breaking join links
 *   - These URLs are not logged; redact query params in any diagnostics
 */

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * Allowed Zoom hostnames in addition to `zoom.us`.
 * Set `VITE_ZOOM_CUSTOM_DOMAINS` (comma-separated) to add org-specific domains.
 * Example: `mycompany.zoom.us`
 */
function getAllowedZoomDomains(): Set<string> {
  const extra =
    typeof import.meta !== 'undefined' && import.meta.env != null
      ? (import.meta.env.VITE_ZOOM_CUSTOM_DOMAINS as string | undefined) ?? ''
      : '';
  const domains = new Set(['zoom.us']);
  for (const d of extra.split(',').map((s) => s.trim()).filter(Boolean)) {
    domains.add(d);
  }
  return domains;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** Returns true if `url` is a valid HTTPS Zoom join URL (zoom.us/j/...). */
export function isZoomJoinUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return false;
    const allowed = getAllowedZoomDomains();
    const isAllowedDomain =
      allowed.has(parsed.hostname) ||
      [...allowed].some(
        (d) => parsed.hostname === d || parsed.hostname.endsWith(`.${d}`),
      );
    if (!isAllowedDomain) return false;
    // Must be a /j/<meetingId> path
    return /^\/j\/\d/.test(parsed.pathname);
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------

/**
 * Normalize a Zoom join URL:
 * - Enforces https:
 * - Preserves meeting ID and all query params (pwd, tk, zak, etc.)
 * - Strips fragments (hashes are not meaningful on zoom.us join URLs)
 * - Returns the input unchanged if it is already canonical
 *
 * Throws if the input is not a valid Zoom join URL.
 */
export function normalizeZoomJoinUrl(url: string): string {
  if (!isZoomJoinUrl(url)) {
    throw new Error(`Not a valid Zoom join URL: ${redactZoomUrlForLog(url)}`);
  }
  const parsed = new URL(url);
  parsed.protocol = 'https:';
  parsed.hash = ''; // strip fragments
  return parsed.toString();
}

/**
 * Returns the normalized Zoom join URL, or `undefined` if the input is absent
 * or not a valid Zoom join URL.
 *
 * This is the safe accessor for use in components: it never throws.
 */
export function getZoomJoinHref(url: string | undefined | null): string | undefined {
  if (!url) return undefined;
  const trimmed = url.trim();
  if (!isZoomJoinUrl(trimmed)) return undefined;
  try {
    return normalizeZoomJoinUrl(trimmed);
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Device detection
// ---------------------------------------------------------------------------

/**
 * Returns true when the current environment is likely a mobile or touch device.
 *
 * Heuristic only — used to choose between "Open in Zoom" and "Join video session"
 * button labels. The behavior (HTTPS anchor) is the same on all platforms.
 */
export function isLikelyMobileOrTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    /Mobi|Android|iPhone|iPad|iPod|Windows Phone/i.test(navigator.userAgent)
  );
}

// ---------------------------------------------------------------------------
// Diagnostics (safe for logging — redacts sensitive params)
// ---------------------------------------------------------------------------

/** Query params that may contain tokens/passwords and must be redacted in logs. */
const SENSITIVE_PARAMS = new Set(['pwd', 'tk', 'zak', 'uname']);

/**
 * Returns a loggable representation of a Zoom URL with sensitive query params
 * replaced by `[redacted]`.
 *
 * Use this instead of the raw URL in any console logs, error messages, or
 * test reports.
 */
export function redactZoomUrlForLog(url: string): string {
  try {
    const parsed = new URL(url);
    for (const key of SENSITIVE_PARAMS) {
      if (parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, '[redacted]');
      }
    }
    return parsed.toString();
  } catch {
    // If the URL can't be parsed, redact everything after '?'
    return url.replace(/\?.*$/, '?[redacted]');
  }
}
